import { useEffect } from 'react';

import { Col, FormInstance, Row, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import IconPlants from '@/assets/icons/icon-plants.svg?react';
import IconScenarios from '@/assets/icons/icon-scenarios.svg?react';
import IconStorageSystems from '@/assets/icons/icon-storage-systems.svg?react';
import IconSuccess from '@/assets/icons/icon-success.svg?react';
import IconWarning from '@/assets/icons/icon-warning.svg?react';
import { useAssets } from '@/hooks/useAssets';

type Props = {
  setIsAssetsValid: (check: boolean) => void;
  form: FormInstance;
};

const AssetsInformation: React.FC<Props> = ({ setIsAssetsValid, form }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const simulationStartDate = form.getFieldValue('simulationStartDate');
  const years = form.getFieldValue('years');
  const {
    plantsData = { count: 0 },
    scenarioData = { count: 0 },
    storageSystemData = { count: 0 },
  } = useAssets(simulationStartDate, years);

  useEffect(() => {
    setIsAssetsValid(scenarioData.count > 0 && storageSystemData.count > 0);
  }, [scenarioData.count, storageSystemData.count, setIsAssetsValid]);

  const renderAssetRow = (
    icon: React.ReactNode,
    count: number,
    label: string,
    description: string
  ) => (
    <Row className="mt-24" align={'middle'}>
      <Col className="mt-8">{icon}</Col>
      <Col className="ml-8">
        <Typography.Text className="text-black-color">
          {count} {label}{' '}
          {t('components.newSimulation.assetsInformation.found')}
        </Typography.Text>
        <Row align={'middle'}>
          {count === 0 ? (
            <IconWarning
              fill={theme.colors.yellowAccent}
              height={'16px'}
              width={'16px'}
            />
          ) : (
            <IconSuccess
              fill={theme.colors.tagSuccess}
              height={'16px'}
              width={'16px'}
            />
          )}
          <Typography.Text className="ml-8 smoke">
            {description}
          </Typography.Text>
        </Row>
      </Col>
    </Row>
  );

  return (
    <div>
      <Typography.Text className="fs-14-bold text-black-color">
        {t('components.newSimulation.assetsInformation.title')}
      </Typography.Text>

      {renderAssetRow(
        <IconPlants fill={theme.colors.dark} height={'32px'} width={'32px'} />,
        plantsData.count,
        t('components.newSimulation.assetsInformation.assets.plant.name'),
        t('components.newSimulation.assetsInformation.assets.plant.description')
      )}

      {renderAssetRow(
        <IconScenarios
          fill={theme.colors.dark}
          height={'32px'}
          width={'32px'}
        />,
        scenarioData.count,
        t('components.newSimulation.assetsInformation.assets.scenario.name'),
        t(
          'components.newSimulation.assetsInformation.assets.scenario.description'
        )
      )}

      {renderAssetRow(
        <IconStorageSystems
          fill={theme.colors.dark}
          height={'32px'}
          width={'32px'}
        />,
        storageSystemData.count,
        t(
          'components.newSimulation.assetsInformation.assets.storageSystem.name'
        ),
        t(
          'components.newSimulation.assetsInformation.assets.storageSystem.description'
        )
      )}
    </div>
  );
};

export default AssetsInformation;
